import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {HistoryDirection} from '@PosterWhiteboard/poster/poster-history/poster-history.types';
import type {HistoryStates} from '@PosterWhiteboard/poster/poster-history/poster-history.types';
import {HistoryStatePoster} from '@PosterWhiteboard/poster/poster-history/history-state-poster';
import {HistoryStatePosterDiff} from '@PosterWhiteboard/poster/poster-history/history-state-poster-diff';
import {HistoryStateDrawing} from '@PosterWhiteboard/poster/poster-history/history-state-drawing';
import {HistoryStatePosterOps} from '@PosterWhiteboard/poster/poster-history/history-state-poster-ops';
import type {DeepPartial} from '@/global';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import isPlainObject from 'lodash/isPlainObject';
import {diff, patch} from 'jsondiffpatch';
import type {Delta} from 'jsondiffpatch';
import {areDevAssertionsEnabled} from '@PosterWhiteboard/libraries/dev-assertions';
import {applyPosterOps} from '@PosterWhiteboard/collaboration/apply-poster-ops.library';
import {derivePosterOps} from '@PosterWhiteboard/collaboration/derive-poster-ops.library';
import {invertPosterOps} from '@PosterWhiteboard/collaboration/invert-poster-ops.library';
import {normalizePosterSnapshot} from '@PosterWhiteboard/collaboration/normalize-poster-snapshot.library';
import {withoutTransientItemState} from '@PosterWhiteboard/collaboration/poster-snapshot.library';
import type {PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';

const LOADING_TOAST_DELAY = 300;

interface ItemLocation {
  pageId: string;
  itemId: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return isPlainObject(value);
};

/**
 * Writes changed leaf values into a snapshot record in place. A nested record the snapshot does
 * not hold is skipped rather than created: a half-filled record there would be applied to the
 * live poster on the next undo.
 */
const writeChangedValuesInto = (snapshotRecord: Record<string, unknown>, changedValues: Record<string, unknown>): void => {
  for (const [key, changedValue] of Object.entries(changedValues)) {
    const snapshotValue = snapshotRecord[key];
    if (!isRecord(changedValue)) {
      if (changedValue !== undefined) {
        snapshotRecord[key] = changedValue;
      }
    } else if (isRecord(snapshotValue)) {
      writeChangedValuesInto(snapshotValue, changedValue);
    }
  }
};

export class PosterHistory {
  private currentHistoryIndex = -1;
  private posterHistoryStates: HistoryStates[] = [];
  private poster: Poster;
  private processing = false;
  private currentSnapshot: DeepPartial<PosterLoadObject> | null = null;

  constructor(poster: Poster) {
    this.poster = poster;
  }

  public addPosterHistory(): void {
    const newState = this.getPosterObjectForHistory();

    if (this.currentSnapshot) {
      const delta = diff(this.currentSnapshot, newState);
      if (delta === undefined) {
        return;
      }
      this.#assertCommitRoundTripsThroughPosterOps(this.currentSnapshot, newState);
      this.posterHistoryStates.length = this.currentHistoryIndex + 1;
      this.posterHistoryStates.push(this.#buildCommitState(this.currentSnapshot, newState, delta));
      this.poster.collaboration.liveEdits.onLocalCommit(this.currentSnapshot, newState);
    } else {
      this.posterHistoryStates.length = this.currentHistoryIndex + 1;
      this.posterHistoryStates.push(new HistoryStatePoster(newState));
    }

    this.currentSnapshot = newState;
    this.currentHistoryIndex = this.getMaxHistoryIndex();
    this.poster.redux.updateUndoRedoStates();
  }

  /**
   * While someone else is in the poster a commit is kept as ops, so undo can be re-expressed
   * against whatever the poster has become; alone, the snapshot delta is enough.
   */
  #buildCommitState(before: DeepPartial<PosterLoadObject>, after: DeepPartial<PosterLoadObject>, delta: Delta): HistoryStatePosterDiff | HistoryStatePosterOps {
    if (!this.poster.collaboration.isSharedWithOthers()) {
      return new HistoryStatePosterDiff(delta);
    }
    const ops = derivePosterOps(before, after);
    return new HistoryStatePosterOps(ops, invertPosterOps(ops, before));
  }

  /** The poster as this history last committed it: the state the collaboration layer starts from. */
  public getCurrentSnapshot(): SnapshotRecord {
    return this.currentSnapshot ?? this.getPosterObjectForHistory();
  }

  /**
   * Shows the shared poster as the collaboration layer now holds it. Other people's edits move
   * the snapshot without minting an undo entry: undo here only ever takes back this tab's own
   * commits.
   */
  public async applyCollaborativeSnapshot(snapshot: SnapshotRecord): Promise<void> {
    if (!this.currentSnapshot) {
      return;
    }
    const delta = diff(this.currentSnapshot, snapshot);
    if (delta === undefined) {
      return;
    }
    // A copy: the collaboration layer keeps building on its own snapshot, and the live poster must not share records with either.
    const targetState = structuredClone(snapshot);
    const previousSnapshot = this.currentSnapshot;
    this.currentSnapshot = targetState;
    const pageIdsToUpdate = this.getAffectedPageIds(delta);
    try {
      await this.poster.updateFromObject(targetState, {undoable: false, refreshActiveSelection: true, pageIdsToUpdate});
    } catch (e) {
      this.currentSnapshot = previousSnapshot;
      throw e;
    }
    // Outlines drawn around these items (another editor's selection) are re-measured now, not on that editor's next presence message.
    for (const pageId of pageIdsToUpdate ?? []) {
      this.poster.redux.notifyItemTransformChanged(pageId);
    }
    await this.poster.getCurrentPage().clampCurrentTime();
  }

  /**
   * Mirrors a change the user did not make (an upload finishing, for example) into the snapshot
   * without minting an undo entry. Such a change is applied to the live item with `undoable: false`;
   * left out of the snapshot it would be folded into the user's next unrelated edit, and undoing
   * that edit would then revert it.
   */
  public recordNonUndoableItemChange({pageId, itemId}: ItemLocation, changedValues: DeepPartial<ItemObject>): void {
    if (!this.currentSnapshot?.pages?.pagesHashMap?.[pageId]?.items?.itemsHashMap?.[itemId]) {
      return;
    }
    const previousSnapshot = this.currentSnapshot;
    const snapshot = structuredClone(previousSnapshot);
    const snapshotItem: unknown = snapshot.pages?.pagesHashMap?.[pageId]?.items?.itemsHashMap?.[itemId];
    if (isRecord(snapshotItem) && isRecord(changedValues)) {
      writeChangedValuesInto(snapshotItem, changedValues);
    }
    this.currentSnapshot = snapshot;
    // Not undoable, but still an edit the saved poster must get: the resolved file replaces the upload the add op left out.
    this.poster.collaboration.liveEdits.onLocalCommit(previousSnapshot, snapshot);
  }

  public async addDrawingHistory(): Promise<void> {
    this.posterHistoryStates.length = this.currentHistoryIndex + 1;
    this.posterHistoryStates.push(new HistoryStateDrawing(await this.poster.drawing.getDrawnFabricObjects()));
    this.currentHistoryIndex = this.getMaxHistoryIndex();
    this.poster.redux.updateUndoRedoStates();
  }

  public reset(): void {
    const state = this.getPosterObjectForHistory();
    this.posterHistoryStates = [new HistoryStatePoster(state)];
    this.currentSnapshot = state;
    this.currentHistoryIndex = this.getMaxHistoryIndex();
    this.poster.redux.updateUndoRedoStates();
  }

  private getPosterObjectForHistory(): DeepPartial<PosterLoadObject> {
    const cleanedPosterObject: DeepPartial<PosterLoadObject> = this.poster.toObjectForLoad();

    if (cleanedPosterObject.pages?.pagesHashMap) {
      delete cleanedPosterObject.id;
      delete cleanedPosterObject.hashedID;
      delete cleanedPosterObject.seoName;
      delete cleanedPosterObject.idLastModifier;
      delete cleanedPosterObject.createdOn;
      delete cleanedPosterObject.lastModified;

      for (const page of Object.values(cleanedPosterObject.pages.pagesHashMap)) {
        delete page?.isPlaying;
        delete page?.zoom;
        // Playhead position is volatile UI state (page.updateFromObject discards it on load),
        // so keeping it would mint an undo entry for a seek that restores nothing visible.
        delete page?.currentTime;
        // Bumped by onPageModified AFTER every history commit, so keeping it would put a
        // spurious timestamp change in every delta and permanently drift currentSnapshot
        // from the live poster (tripping the undo/redo sync assertion).
        delete page?.lastModified;
        // Ephemeral timeline selection: updateFromObject discards it on apply, so keeping
        // it would only mint diff noise for selection changes between commits. (Audio
        // selection lives in a flat redux slice now — never serialized here at all.)
        delete page?.transitions?.selectedBoundaryId;

        if (page?.items?.itemsHashMap) {
          for (const item of Object.values(page.items.itemsHashMap)) {
            if (!item) continue;

            if (item.gitype === ITEM_TYPE.IMAGE || item.gitype === ITEM_TYPE.IMAGESLIDE || item.gitype === ITEM_TYPE.VIDEO || item.gitype === ITEM_TYPE.VIDEOSLIDE) {
              if ('removeBackground' in item && item.removeBackground) {
                delete item.removeBackground.isBackgroundBeingRemoved;
                delete item.removeBackground.pollingKey;
              }
            }

            if (item.gitype === ITEM_TYPE.VIDEO || item.gitype === ITEM_TYPE.VIDEOSLIDE) {
              // Derived from the loaded media element, not a user edit — undo must never toggle it.
              delete (item as Record<string, unknown>).hasAudio;
              delete (item as Record<string, unknown>).timelineSpriteHashedFilename;
              delete (item as Record<string, unknown>).timelineSpriteFrameWidth;
              delete (item as Record<string, unknown>).timelineSpriteFrameHeight;
              delete (item as Record<string, unknown>).timelineSpriteNumSprites;
              delete (item as Record<string, unknown>).timelineSpriteFps;
            }
          }
        }
      }
    }

    return cleanedPosterObject;
  }

  public hasUndo(): boolean {
    return this.currentHistoryIndex > 0;
  }

  public hasRedo(): boolean {
    return this.currentHistoryIndex < this.getMaxHistoryIndex();
  }

  async redo(): Promise<void> {
    if (this.processing) {
      return;
    }
    this.#assertSnapshotMatchesLivePoster('redo');

    this.processing = true;
    try {
      showLoading('redoChanges', {delay: LOADING_TOAST_DELAY});
      const historyState = this.posterHistoryStates[this.currentHistoryIndex + 1];
      if (historyState) {
        await this.applyHistoryState(historyState, HistoryDirection.REDO);
        this.currentHistoryIndex += 1;
        this.poster.redux.updateUndoRedoStates();
        this.poster.redux.markHistoryNavigation();
      }
    } finally {
      this.processing = false;
      hideLoading('redoChanges');
    }
  }

  async undo(): Promise<void> {
    // Without this guard, undoing past the baseline applies the full initial snapshot, sets the
    // index to -1, and the next edit truncates the whole history array — wiping the baseline.
    if (this.processing || !this.hasUndo()) {
      return;
    }
    this.#assertSnapshotMatchesLivePoster('undo');

    this.processing = true;
    try {
      showLoading('undoChanges', {delay: LOADING_TOAST_DELAY});
      const historyState = this.posterHistoryStates[this.currentHistoryIndex];

      if (historyState instanceof HistoryStateDrawing) {
        await this.undoDrawingState();
        this.poster.redux.markHistoryNavigation();
      } else if (historyState) {
        await this.applyHistoryState(historyState, HistoryDirection.UNDO);
        this.currentHistoryIndex -= 1;
        this.poster.redux.updateUndoRedoStates();
        this.poster.redux.markHistoryNavigation();
      }
    } finally {
      this.processing = false;
      hideLoading('undoChanges');
    }
  }

  private async undoDrawingState(): Promise<void> {
    const prevHistoryState = this.currentHistoryIndex > 0 ? this.posterHistoryStates[this.currentHistoryIndex - 1] : undefined;
    if (prevHistoryState instanceof HistoryStateDrawing) {
      await this.applyHistoryState(prevHistoryState, HistoryDirection.UNDO);
    } else {
      await this.poster.drawing.updateCurrentDrawingFromFabricObjects({});
    }
    this.currentHistoryIndex -= 1;
    this.poster.redux.updateUndoRedoStates();
  }

  removeDrawingHistoryStates(): void {
    const newPosterHistoryStates: HistoryStates[] = [];
    let newCurrentHistoryIndex = -1;

    for (const [index, posterHistoryState] of this.posterHistoryStates.entries()) {
      if (!(posterHistoryState instanceof HistoryStateDrawing)) {
        if (index <= this.currentHistoryIndex) {
          newCurrentHistoryIndex = newPosterHistoryStates.length;
        }
        newPosterHistoryStates.push(posterHistoryState);
      }
    }

    this.currentHistoryIndex = newCurrentHistoryIndex;
    this.posterHistoryStates = newPosterHistoryStates;
  }

  async applyHistoryState(historyState: HistoryStates, direction: HistoryDirection): Promise<void> {
    if (historyState instanceof HistoryStatePosterOps) {
      await this.#applyOpsHistoryState(direction === HistoryDirection.UNDO ? historyState.inverseOps : historyState.ops);
    } else if (historyState instanceof HistoryStatePosterDiff) {
      // Diff states are only ever created after a snapshot exists (see addPosterHistory).
      if (!this.currentSnapshot) {
        return;
      }
      const delta = direction === HistoryDirection.UNDO ? historyState.getPatchForUndo() : historyState.patchForRedo;
      const targetState = structuredClone(this.currentSnapshot);
      patch(targetState, delta);
      const pageIdsToUpdate = this.getAffectedPageIds(delta);
      const previousSnapshot = this.currentSnapshot;
      this.currentSnapshot = targetState;
      try {
        await this.poster.updateFromObject(targetState, {undoable: false, refreshActiveSelection: true, pageIdsToUpdate});
      } catch (e) {
        this.currentSnapshot = previousSnapshot;
        throw e;
      }
      this.poster.collaboration.liveEdits.onLocalCommit(previousSnapshot, targetState);
    } else if (historyState instanceof HistoryStatePoster) {
      const previousSnapshot = this.currentSnapshot;
      this.currentSnapshot = historyState.posterLoadObject;
      try {
        await this.poster.updateFromObject(historyState.posterLoadObject, {undoable: false, refreshActiveSelection: true});
      } catch (e) {
        this.currentSnapshot = previousSnapshot;
        throw e;
      }
      if (previousSnapshot) {
        this.poster.collaboration.liveEdits.onLocalCommit(previousSnapshot, historyState.posterLoadObject);
      }
    } else if (historyState instanceof HistoryStateDrawing) {
      await this.poster.drawing.updateCurrentDrawingFromFabricObjects(historyState.drawnFabricObjects);
    }

    await this.poster.getCurrentPage().clampCurrentTime();
  }

  /**
   * Undo or redo of a commit made while editing together: the ops are applied to the poster as it
   * is now (an item someone else deleted is simply skipped) and the outcome is sent as a new commit.
   */
  async #applyOpsHistoryState(ops: PosterOp[]): Promise<void> {
    if (!this.currentSnapshot) {
      return;
    }
    const previousSnapshot = this.currentSnapshot;
    const targetState = normalizePosterSnapshot(applyPosterOps(previousSnapshot, ops));
    const delta = diff(previousSnapshot, targetState);
    if (delta === undefined) {
      return;
    }
    this.currentSnapshot = targetState;
    try {
      await this.poster.updateFromObject(targetState, {undoable: false, refreshActiveSelection: true, pageIdsToUpdate: this.getAffectedPageIds(delta)});
    } catch (e) {
      this.currentSnapshot = previousSnapshot;
      throw e;
    }
    this.poster.collaboration.liveEdits.onLocalCommit(previousSnapshot, targetState);
  }

  private getAffectedPageIds(delta: Delta): string[] | undefined {
    if (!delta) {
      return undefined;
    }

    const deltaObj = delta as Record<string, unknown>;
    const pagesChanges = deltaObj.pages as Record<string, unknown> | undefined;
    if (!pagesChanges) {
      return [];
    }

    const pagesHashMapChanges = pagesChanges.pagesHashMap as Record<string, unknown> | undefined;
    if (!pagesHashMapChanges) {
      // Only pageOrder changed, no page content updates needed.
      return [];
    }

    return Object.keys(pagesHashMapChanges);
  }

  private getMaxHistoryIndex(): number {
    return this.posterHistoryStates.length - 1;
  }

  /**
   * Diff-based history is only correct while currentSnapshot deep-equals the live poster; an
   * undoable:false mutation with no trailing commit silently breaks that and makes undo/redo
   * revert the wrong state. Dev/test-only: reports the drift at the moment it matters, with
   * the offending paths, instead of leaving a mystery corruption for later.
   */
  #assertSnapshotMatchesLivePoster(operation: 'undo' | 'redo'): void {
    if (!areDevAssertionsEnabled() || !this.currentSnapshot) {
      return;
    }
    const drift = diff(this.currentSnapshot, this.getPosterObjectForHistory());
    if (drift !== undefined) {
      console.error(
        `PosterHistory ${operation}: snapshot is out of sync with the live poster — an undoable:false mutation was not followed by a commit, so ${operation} may revert the wrong state. Drifted paths:`,
        drift
      );
    }
  }

  /**
   * Collaborative editing sends each commit as poster ops instead of a snapshot delta, so every
   * commit must survive the conversion losslessly in both directions (redo and undo). Dev/test-only:
   * a commit the op format cannot express is reported here, on the edit that produced it.
   */
  #assertCommitRoundTripsThroughPosterOps(rawBefore: DeepPartial<PosterLoadObject>, rawAfter: DeepPartial<PosterLoadObject>): void {
    if (!areDevAssertionsEnabled()) {
      return;
    }
    const before = withoutTransientItemState(rawBefore);
    const after = withoutTransientItemState(rawAfter);
    const ops = derivePosterOps(before, after);
    const redoDrift = diff(applyPosterOps(before, ops), after);
    const undoDrift = diff(applyPosterOps(after, invertPosterOps(ops, before)), before);
    if (redoDrift !== undefined || undoDrift !== undefined) {
      console.error('PosterHistory commit: poster ops do not reproduce this commit, so it would not sync or undo correctly for collaborators. Drifted paths:', {redoDrift, undoDrift});
    }
  }
}
